import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { DeleteQuestionnaireFromHistoryModal } from "@/components/feature/Modals/DeleteQuestionnaireFromHistoryModal/DeleteQuestionnaireFromHistoryModal"
import { PERMISSIONS } from "@/constants/permissions"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { track } from "@/services/analytics/amplitude"
import {
  useExportFilledPdf,
  useGetQuestionnaireDocument,
} from "@/services/api/questionnaireService"
import {
  Questionnaire,
  QuestionnaireStatus,
} from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { downloadFile, downloadFileFromUrl } from "@/utils/downloadUtils"
import { isPdfFileName } from "@/utils/fileUtils"
import { createDownloadDocumentName } from "@/utils/questionnaire/fileName"
import {
  getRouteByStatus,
  getStatusIconByStatus,
} from "@/utils/questionnaire/questionnaireStatus"
import { IconFileDownload, IconTrash } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"

interface QuestionnaireListItemProps {
  item: Questionnaire
}

export const QuestionnaireListItem: React.FC<QuestionnaireListItemProps> = ({
  item,
}) => {
  const { translate } = useTranslation()
  const [isHover, setHover] = useState(false)
  const navigate = useNavigate()
  const [fileDownloadEnabled, setFileDownloadEnabled] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const featureFlags = useGetAllFeatureFlags()

  const shouldUseExportRoute =
    isPdfFileName(item.fileName) && !!item.hasPositionData

  const {
    data: questionnaireDocument,
    isPending: isDocumentPending,
    isError: isDocumentError,
  } = useGetQuestionnaireDocument(
    item.id,
    fileDownloadEnabled && !shouldUseExportRoute,
  )

  const {
    data: exportedFilledPdf,
    isPending: isExportFilledPdfPending,
    isError: isExportFilledPdfError,
  } = useExportFilledPdf(item.id, fileDownloadEnabled && shouldUseExportRoute)
  const queryClient = useQueryClient()

  const Icon = getStatusIconByStatus(item.status)

  const handleItemClick = () => {
    queryClient.removeQueries({
      queryKey: ["questionnaire", { questionnaireId: item.id }],
    })
    queryClient.removeQueries({
      queryKey: ["questionnaireDoc"],
    })
    queryClient.removeQueries({
      queryKey: ["questionnaireSourceFile"],
    })
    queryClient.removeQueries({
      queryKey: ["originalFile"],
    })

    navigate(
      getRouteByStatus(
        item.id,
        item.status,
        item.fileName,
        item?.hasPositionData,
        undefined,
        featureFlags,
      ),
    )
  }

  useEffect(() => {
    if (isDocumentError || isExportFilledPdfError) {
      toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
    }
  }, [isDocumentError, isExportFilledPdfError, translate])

  const handleDownloadButtonClick = () => {
    track("QUESTIONNAIRE_DOWNLOAD")
    setFileDownloadEnabled(true)
  }

  useEffect(() => {
    const downloadUrl = shouldUseExportRoute
      ? exportedFilledPdf
      : questionnaireDocument

    if (downloadUrl && fileDownloadEnabled) {
      const fileName = createDownloadDocumentName(item)

      const triggerDownload = async () => {
        try {
          if (shouldUseExportRoute) {
            await downloadFileFromUrl(downloadUrl, fileName)
          } else {
            downloadFile(downloadUrl, fileName)
          }
        } catch {
          toast.error(translate("QUESTIONNAIRE_PREVIEW/FETCH_DOCUMENT_ERROR"))
        } finally {
          setFileDownloadEnabled(false)
        }
      }

      triggerDownload()
    }
  }, [
    exportedFilledPdf,
    fileDownloadEnabled,
    item,
    questionnaireDocument,
    shouldUseExportRoute,
    translate,
  ])

  return (
    <>
      {isDeleteModalOpen && (
        <DeleteQuestionnaireFromHistoryModal
          questionnaireId={item.id}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          dataTestId={"questionnaire-delete-modal"}
        />
      )}
      <div
        className={classNames(
          "flex items-center justify-between",
          "hover:bg-yellow1/10",
          "px-S py-M",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
      >
        <div className="flex cursor-pointer items-center">
          <div className="pr-S">
            <Icon />
          </div>
          <div className="flex flex-col space-y-1" onClick={handleItemClick}>
            <Typography variant="textS" className="hover:underline">
              {item.fileName}
            </Typography>
            <Typography variant={"textXS"} className="text-grey5">
              {`${translate("HISTORY/UPLOADED_BY")} ${item.userName}`}
            </Typography>
          </div>
        </div>
        <div className="flex w-2/6 justify-end space-x-4">
          {isHover ? (
            <>
              <PermissionGate permission={PERMISSIONS.QUEST_DELETE}>
                <button
                  className="hover:cursor-pointer focus:outline-solid"
                  onClick={() => setIsDeleteModalOpen(true)}
                  tabIndex={0}
                  data-testid="questionnaire-delete-button"
                  aria-label={translate(
                    "HISTORY/FILE_ARIA_LABEL_DELETE_BUTTON",
                  )}
                >
                  <IconTrash size={24} stroke={1} aria-hidden="true" />
                </button>
              </PermissionGate>
              {item.status === QuestionnaireStatus.Completed && (
                <>
                  {fileDownloadEnabled &&
                  (isDocumentPending || isExportFilledPdfPending) ? (
                    <LoadingSpinner size={24} />
                  ) : (
                    <button
                      className="hover:cursor-pointer focus:outline-solid"
                      onClick={handleDownloadButtonClick}
                      tabIndex={0}
                      aria-label={translate(
                        "HISTORY/FILE_ARIA_LABEL_DOWNLOAD_BUTTON",
                      )}
                      data-testid="questionnaire-download-button"
                    >
                      <IconFileDownload
                        size={24}
                        stroke={1}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="flex items-center space-x-2">
              {/*<Pill*/}
              {/*  textColor={getQuestionnaireStatusPillVariant(item.status).font}*/}
              {/*  backgroundColor={*/}
              {/*    getQuestionnaireStatusPillVariant(item.status).background*/}
              {/*  }*/}
              {/*>*/}
              {/*  {translate(*/}
              {/*    getQuestionnaireStatusTranslationKey(*/}
              {/*      stringToQuestionnaireStatus(item.status),*/}
              {/*    ),*/}
              {/*  )}*/}
              {/*</Pill>*/}
              <Typography as="p" variant="textS">
                {item.createdAt.toLocaleDateString("de")}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
